import { useEffect, useState } from 'react';
import { toast } from '../../../../../../../ui/toast';
import {
  useCreateOrUpdateCCAConfigMutation,
  useQuickBuildCCAConfigMutation,
} from '../../../../../../../utils/api_service/ccaApi';
import { CCAConfig, CCAQuickConfig } from '../../../../../../../utils/types/cca_types';
import {
  CCASubdomain,
  CCA_SUBDOMAINS,
} from '../../../../../../questions/CCABank/ccaConstants';
import {
  QuickBuildConfig,
  defaultQuickBuildConfig,
  toTitleCase,
} from '../utils/ccaHelpers';

interface UseQuickBuildProps {
  jobOpeningId: string;
  existingConfig?: CCAConfig;
}

export const useQuickBuild = ({ jobOpeningId, existingConfig }: UseQuickBuildProps) => {
  const [config, setConfig] = useState<QuickBuildConfig>(defaultQuickBuildConfig);
  const [isGenerating, setIsGenerating] = useState(false);

  const [saveConfig] = useCreateOrUpdateCCAConfigMutation();
  const [quickBuild] = useQuickBuildCCAConfigMutation();

  // Pre-populate config settings from existing quickConfig
  useEffect(() => {
    if (!existingConfig?.quickConfig?.subdomains?.length) return;
    const sds = existingConfig.quickConfig.subdomains;
    const first = sds[0];
    const total = sds.reduce((sum, s) => sum + s.numQuestions, 0);
    const saCount = Math.round((1 - first.mcRatio) * total);
    const selectedSds = sds
      .map((s) => toTitleCase(s.subdomain) as CCASubdomain)
      .filter((s) => CCA_SUBDOMAINS.includes(s as CCASubdomain));
    setConfig((prev) => ({
      ...prev,
      subdomains: selectedSds.length ? selectedSds : prev.subdomains,
      level: first.level,
      timeLimitSeconds: first.timeLimitSeconds,
      totalQuestions: total,
      shortAnswerCount: saCount,
    }));
  }, [existingConfig]); // eslint-disable-line

  const mcCount = config.totalQuestions - config.shortAnswerCount;
  const imageCount = config.totalQuestions - config.wordyCount;

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const baseCount = Math.floor(
        config.totalQuestions / Math.max(1, config.subdomains.length),
      );
      const remainder =
        config.totalQuestions - baseCount * config.subdomains.length;
      const quickConfig: CCAQuickConfig = {
        subdomains: config.subdomains.map((sd, i) => ({
          subdomain: sd.toLowerCase().replace(/ /g, '_'),
          numQuestions: i === 0 ? baseCount + remainder : baseCount,
          level: config.level,
          mcRatio:
            config.totalQuestions > 0 ? mcCount / config.totalQuestions : 0,
          timeLimitSeconds: config.timeLimitSeconds,
        })),
      };
      await saveConfig({
        jobOpeningId,
        body: { enabled: true, quickConfig },
      }).unwrap();
      const buildResult = await quickBuild(jobOpeningId).unwrap();
      if (buildResult.built > 0) {
        toast.success(
          `${buildResult.built} question${buildResult.built !== 1 ? 's' : ''} generated successfully.`,
          { position: 'bottom-center' },
        );
      } else {
        toast.warn(
          'No questions matched the selected criteria. Try adjusting subdomain, level, or type settings.',
          { position: 'bottom-center' },
        );
      }
    } catch {
      toast.error('Failed to generate questions.', { position: 'bottom-center' });
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleSubdomain = (sd: CCASubdomain) => {
    setConfig((prev) => ({
      ...prev,
      subdomains: prev.subdomains.includes(sd)
        ? prev.subdomains.filter((s) => s !== sd)
        : [...prev.subdomains, sd],
    }));
  };

  return {
    config,
    setConfig,
    isGenerating,
    mcCount,
    imageCount,
    handleGenerate,
    toggleSubdomain,
  };
};
